import {DeleteOutlined} from '@ant-design/icons';
import {EditOutlined} from '@ant-design/icons/lib';
import {Button, Card, Divider, Modal, Space} from 'antd';
import {UpdateAccountInput} from 'api/generated';
import {AnchorLink, PhoneNumberInput, PropertyList, Stack} from 'components';
import {Formik} from 'formik';
import {Form, FormItem, Input, SubmitButton} from 'formik-antd';
import {useAccessLevel} from 'hooks/useAccessLevel';
import {colors} from 'lib/colors';
import {formatEmail, formatPhone} from 'lib/formaters';
import React, {useState} from 'react';
import * as yup from 'yup';
import {SectionProps} from './types';

const schema = yup.object().shape({
  contactPerson: yup.object().shape({
    name: yup.string().required(),
    email: yup.string().email().required(),
    phone: yup.string()
  })
});

export const ContactPerson: React.FC<SectionProps> = ({
  cardProps,
  formProps,
  account,
  onSubmit
}) => {
  const [isEditing, setEditing] = useState(false);
  const {isAdmin} = useAccessLevel();

  if (!account) return <Card {...cardProps} title="Main contact person" />;

  const initialValues: UpdateAccountInput = {
    id: account.id,
    contactPerson: account?.contactPerson || {name: '', email: '', phone: ''}
  };

  const handleSubmit = async (values: UpdateAccountInput) => {
    await onSubmit(values);
    setEditing(false);
  };

  const handleDelete = async () => {
    Modal.confirm({
      title: 'Are you sure?',
      content: 'This will delete the contact person from the account.',
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: () => {
        return onSubmit({id: account.id, contactPerson: null});
      }
    });
  };

  return (
    <Card
      {...cardProps}
      title="Main contact person"
      extra={
        isEditing || !isAdmin ? null : (
          <Space>
            {account.contactPerson && (
              <>
                <AnchorLink onClick={handleDelete} style={{color: colors.red}}>
                  <DeleteOutlined /> Delete
                </AnchorLink>
                <Divider type="vertical" />
              </>
            )}
            <AnchorLink onClick={() => setEditing(true)}>
              <EditOutlined /> {account.contactPerson ? 'Edit data' : 'Add data'}
            </AnchorLink>
          </Space>
        )
      }>
      {isEditing ? (
        <Formik onSubmit={handleSubmit} initialValues={initialValues} validationSchema={schema}>
          <Form {...formProps}>
            <FormItem name="contactPerson.name" label="Name">
              <Input name="contactPerson.name" />
            </FormItem>
            <FormItem name="contactPerson.email" label="Email">
              <Input name="contactPerson.email" type="email" />
            </FormItem>
            <FormItem name="contactPerson.phone" label="Phone">
              <PhoneNumberInput
                name="contactPerson.phone"
                countryCode={account?.business?.country}
              />
            </FormItem>
            <FormItem
              wrapperCol={{offset: formProps.labelCol?.span, ...formProps.wrapperCol}}
              name="submit">
              <Stack>
                <Button onClick={() => setEditing(false)}>Cancel</Button>
                <SubmitButton>Submit</SubmitButton>
              </Stack>
            </FormItem>
          </Form>
        </Formik>
      ) : (
        <PropertyList
          omitEmpty
          emptyText="No contact person provided"
          properties={[
            {
              label: 'Name',
              value: account?.contactPerson?.name
            },
            {
              label: 'Email',
              value: formatEmail(account?.contactPerson?.email)
            },
            {
              label: 'Phone',
              value: formatPhone(account?.contactPerson?.phone, account?.business?.country, true)
            }
          ]}
        />
      )}
    </Card>
  );
};
